<template>
    <transition :name="$route.meta.transition || 'fade'">
        <Loading :loading="loading" :inset="true" class="my-4">
            <div class="container mx-auto min-h-app relative">
                <CheckoutProgress :tabs="tabs" :current-tab="currentTab" :tab-names-phrase-keys="tabNamesPhraseKeys" @change-tab="changeTab" />

                <div class="flex flex-col lg:flex-row gap-x-12">
                    <div class="w-full lg:w-2/3">
                        <div class="py-12">
                            <div class="flex flex-col lg:flex-row gap-4 justify-between items-center mb-4">
                                <h5 id="top" class="font-bold">
                                    {{ $t(tabNamesPhraseKeys[tabs.indexOf(currentTab)]) }}
                                </h5>
                                <span
                                    v-if="currentTab == 'CheckoutRegister'"
                                    class="text-blue flex flex-row gap-2 items-center cursor-pointer mb-2"
                                    @click="changeTab('CheckoutLogin')"
                                >
                                    {{ $t('account_state_true') }}
                                    <Icon type="ArrowRight" viewBox="0 0 29 21" width="20" height="12" class="fill-none stroke-current" />
                                </span>
                                <span v-if="currentTab == 'CheckoutLogin'" class="text-blue flex flex-row gap-2 items-center cursor-pointer" @click="changeTab('CheckoutRegister')">
                                    {{ $t('account_state_false') }}
                                    <Icon type="ArrowRight" viewBox="0 0 29 21" width="20" height="12" class="fill-none stroke-current" />
                                </span>
                            </div>
                            <keep-alive>
                                <component
                                    :is="currentTab"
                                    ref="tab"
                                    v-model:selected-existing-payment-method="selectedExistingPaymentMethod"
                                    v-model:selected-new-payment-method="selectedNewPaymentMethod"
                                    :checkout-account-data-error="checkoutAccountDataError"
                                    @editing-profile="updateEditingProfile"
                                    @update:select-existing-payment-method="selectExistingPaymentMethod"
                                    @update:select-new-payment-method="selectNewPaymentMethod"
                                    @edit-profile="changeTab('UserProfile')"
                                    @edit-paymentmethod="changeTab('Payment')"
                                />
                            </keep-alive>

                            <PaymentProcessExistingPaymentMethod ref="existingPaymentMethodForm" v-model:selected-existing-payment-method="selectedExistingPaymentMethod" />

                            <PaymentProcessNewPaymentMethod ref="newPaymentMethodForm" v-model:selected-new-payment-method="selectedNewPaymentMethod" />
                        </div>
                    </div>

                    <div v-show="!editingProfile" class="w-full lg:w-1/3">
                        <div class="sticky top-[var(--headerHeightCurrent)] pb-12 lg:py-12">
                            <div class="bg-white shadow-md rounded-md p-4 flex flex-col h-full">
                                <div class="flex flex-row justify-between items-center">
                                    <h6 class="text-lg font-bold">
                                        {{ $t('your_order') }}
                                    </h6>
                                    <span>
                                        {{ $t('payment_count_articles', { count: $store.getters['cart/getCount'] }) }}
                                    </span>
                                </div>

                                <!-- Cart -->
                                <Info v-if="cartMessages" v-model:data="cartMessages" class="my-2" :closeable="true" />

                                <div v-if="getItems?.length" class="flex flex-col max-h-full justify-between h-full">
                                    <div class="h-auto overflow-y-auto overflow-x-clip">
                                        <div v-for="cartItem in sortProductItemsByName" :key="cartItem.index">
                                            <CartItem :data="cartItem" :editable="true" />
                                        </div>
                                    </div>

                                    <div class="py-4">
                                        <small class="text-xxxs italic text-gray-600">
                                            {{ $t('payment_disclaimer') }}
                                        </small>
                                    </div>

                                    <div v-if="isLoggedIn" class="flex flex-col border-t border-gray-300 py-4">
                                        <Input
                                            v-if="!get.coupon_code"
                                            v-model:value="coupon"
                                            type="text"
                                            name="code"
                                            :label="$t('discount_code')"
                                            autocomplete="text"
                                            class="m-0 my-4"
                                        >
                                            <button
                                                :class="[
                                                    'absolute right-0 top-0 h-full flex items-center small text-blue text-xs px-2  z-20',
                                                    !coupon?.length ? 'cursor-no-drop text-blue-dark/50' : 'cursor-pointer text-blue'
                                                ]"
                                                :disabled="!coupon?.length"
                                                @click.prevent="applyCoupon"
                                            >
                                                <span>{{ $t('plain_apply') }}</span>
                                            </button>
                                        </Input>
                                    </div>

                                    <CartTotals @reload="$emit('reload')" />
                                    <div v-if="isLoggedIn && currentTab === 'Overview'" id="terms" class="my-4">
                                        <div class="my-4">
                                            <Checkbox v-model:value="acceptedConditions" name="conditions" :label="conditionsText" class="m-0 my-2 text-xs" />
                                            <div v-if="conditionsNotAcceptedError" class="mt-2 text-red text-xs">
                                                <p>
                                                    {{ $t('accept_conditions') }}
                                                </p>
                                            </div>
                                        </div>
                                        <div class="my-4">
                                            <Checkbox v-model:value="acceptedTermsConditions" name="terms_and_conditions" :label="termsText" class="m-0 my-2 text-xs" />
                                            <div v-if="termsNotAcceptedError" class="mt-2 text-red text-xs">
                                                <p>
                                                    {{ $t('accept_terms_and_conditions') }}
                                                </p>
                                            </div>
                                        </div>
                                        <div v-if="region === 'de' && wheel?.minOrderValue <= cartTotalGross" class="my-4">
                                            <Checkbox v-model:value="acceptedWheelConditions" name="wheel_condition" :label="wheelTermsText" class="m-0 my-2 text-xs" />
                                            <div v-if="wheelTermsNotAcceptedError" class="mt-2 text-red text-xs">
                                                <p>Akzeptieren Sie die Bestimmungen zur HKCM-Glücksrad-Aktion, um fortzufahren.</p>
                                            </div>
                                        </div>

                                        <div class="my-4">
                                            <Popper v-if="!receivesNewsletters">
                                                <template #content>
                                                    <div v-html="newsletterText"></div>
                                                </template>
                                                <div class="text-xs flex flex-row gap-1 items-center cursor-pointer">
                                                    <Checkbox
                                                        v-model:value="registerForNewsletter"
                                                        name="newsletter"
                                                        :form-component-id="$.uid"
                                                        :label="$t('subscribe_newsletter_free')"
                                                        class="m-0 my-2 text-xs"
                                                    />
                                                    <Icon type="InfoCircle" viewBox="0 0 20 20" width="15" height="15" class="fill-current" />
                                                </div>
                                            </Popper>
                                        </div>
                                    </div>

                                    <div v-if="accountErrors" class="mt-2 text-red text-xs">
                                        <p>
                                            {{ $t('errors_in_account_data') }}
                                        </p>
                                    </div>

                                    <div v-if="!$isCheckoutAllowed" class="mt-2 text-red text-base">
                                        {{ $t('checkout_disabled_message') }}
                                    </div>
                                    <FormButton
                                        v-if="currentTab === 'Overview' && isLoggedIn && $isCheckoutAllowed"
                                        class="mt-3"
                                        styling="primary flex items-center gap-3 w-full justify-center"
                                        :value="$t('payment_buy_now')"
                                        :disabled="loading"
                                        @click="processPayment()"
                                    >
                                    </FormButton>
                                    <FormButton
                                        v-else-if="isLoggedIn && $isCheckoutAllowed"
                                        class="mt-3"
                                        styling="primary flex items-center gap-3 w-full justify-center"
                                        :value="$t('proceed')"
                                        :disabled="loading"
                                        @click="changeTab(tabs[tabs.indexOf(currentTab) + 1])"
                                    >
                                    </FormButton>
                                    <div v-if="errorMsg" class="mt-3 flex items-start gap-2 rounded-md border border-red p-3 text-sm text-red" style="background-color: #fee2e2">
                                        <i class="fi fi-rr-exclamation text-red mt-0.5"></i>
                                        <span>{{ errorMsg }}</span>
                                    </div>
                                    <div
                                        v-if="displayWarningAboMsg"
                                        class="mt-3 flex items-start gap-2 rounded-md border border-orange-300 bg-orange-50 p-3 text-sm text-orange-700"
                                    >
                                        <i class="fi fi-rr-info text-orange-500 mt-0.5"></i>
                                        <span> {{ $t('checkout_global_warning_glo_de') }} </span>
                                    </div>
                                </div>
                                <div v-else>
                                    <small>
                                        {{ $t('no_items_in_cart') }}
                                    </small>

                                    <FormButton
                                        class="mt-3"
                                        styling="primary flex items-center gap-3 w-full justify-center"
                                        :value="$t('search_products')"
                                        :disabled="false"
                                        @click="$router.push('/produkte')"
                                    >
                                    </FormButton>
                                </div>
                                <!-- Cart End -->
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Popup :overflow-hidden="true" :open="showAutoCouponPopup" class="px-6" @open="$emit('update:open', (showAutoCouponPopup = !showAutoCouponPopup))">
                <div class="relative cursor-pointer">
                    <strong class="py-4 block text-lg mx-auto text-center text-white" style="background: linear-gradient(90deg, rgb(3, 19, 42) 45%, rgb(253, 234, 0) 100%)">
                        {{ $t('good_news') }}
                    </strong>
                    <div class="cursor-pointer m-4 flex flex-col items-center">
                        <div @click="closePopupAndApplyCoupon()">
                            <span class="text-blue-dark font-body my-4 block w-full lg:w-10/12">
                                {{ $t('discount_promotion_missing') }}
                                {{ $t('by_clicking_apply_discount') }}<br /><br />
                                {{ $t('good_luck') }}
                            </span>

                            <FormButton class="mt-3" styling="primary flex items-center gap-3 w-fit justify-center mx-auto" :value="$t('plain_apply')" :disabled="false">
                            </FormButton>
                        </div>
                    </div>
                </div>
            </Popup>
        </Loading>
    </transition>
</template>

<script>
import i18next from 'i18next';
import { mapActions, mapGetters } from 'vuex';
import api from '../api';
import CartTotals from '../components/cart/CartTotals.vue';
import CheckoutProgress from '../components/checkout/CheckoutProgress.vue';
import Toastification from '../components/miscellaneous/Toastification.vue';

export default {
    components: {
        CheckoutProgress,
        CartTotals
    },
    emits: ['update:open'],
    data: () => ({
        currentTab: 'CheckoutLogin',
        tabs: ['CheckoutLogin', 'UserProfile', 'Payment', 'Overview', 'CheckoutSuccess'],
        tabNamesPhraseKeys: ['checkout_tabs.login', 'checkout_tabs.customer_data', 'checkout_tabs.payment', 'checkout_tabs.overview', 'checkout_tabs.finish'],
        loading: false,
        cartMessages: [],
        data: {},
        acceptedTermsConditions: false,
        acceptedConditions: false,
        termsNotAcceptedError: false,
        conditionsNotAcceptedError: false,
        registerForNewsletter: false,
        accountErrors: false,
        coupon: null,
        autoCoupon: null,
        showAutoCouponPopup: false,
        editingProfile: false,
        paymentMethodSelected: false,
        selectedNewPaymentMethod: null,
        selectedExistingPaymentMethod: null,
        checkoutAccountDataError: false,
        errorMsg: '',
        displayWarningAboMsg: false,
        _prevLoggedIn: null,
        _loginEventSent: false, // verhindert Doppelfeuer
        acceptedWheelConditions: false,
        wheelTermsNotAcceptedError: false
    }),
    computed: {
        region() {
            return window.meta.region_configuration.region || 'de';
        },
        sortProductItemsByName() {
            const locale = i18next.language;

            return [...this.getItems].sort((a, b) => a.product.name[locale].toLowerCase().localeCompare(b.product.name[locale].toLowerCase()));
        },
        ...mapGetters('user', {
            user: 'get',
            isLoggedIn: 'isLoggedIn',
            receivesNewsletters: 'receivesNewsletters',
            hasCustomer: 'hasCustomer'
        }),
        ...mapGetters('cart', ['get', 'getItems', 'getMeta', 'getCouponCode']),
        ...mapGetters('payment_methods', {
            getPaymentMethods: 'get'
        }),
        termsText() {
            return i18next.t('contract_execution_before_cancellation');
        },
        conditionsText() {
            return i18next.t('terms_conditions_consent');
        },
        newsletterText() {
            return i18next.t('newsletter_subscription_info');
        },
        wheelTermsText() {
            return (
                'Hiermit akzeptiere ich die <a href="/verlosung" class="text-[#353cee]">Teilnahmebedingungen</a> und\n' +
                '            <a href="/verlosungdatenschutz" class="text-[#353cee]">Datenschutzhinweise</a> der HKCM-Glücksrad-Aktion.'
            );
        },
        wheel() {
            return this.get.wheel || null;
        },
        cartTotalGross() {
            return this.get.total_end_price_gross || 0;
        }
    },

    watch: {
        async isLoggedIn(newVal, oldVal) {
            this.onChangeUserState();
            await this.loadCart();

            // Reset bei Logout
            if (oldVal === true && newVal === false) {
                this._loginEventSent = false;
                return;
            }

            // Fire nur bei echtem Wechsel false/undefined -> true
            const wasLoggedOut = oldVal === false || typeof oldVal === 'undefined';
            if (wasLoggedOut && newVal === true && !this._loginEventSent) {
                this.pushLoginStatus();
                this._loginEventSent = true;
            }
        },
        open: function () {
            if (this.open) {
                this.index();
            }
        },
        getMeta: function () {
            _.forEach(this.getMeta?.message, (message, type) => {
                this.cartMessages.push({
                    message,
                    type
                });
            });
        }
    },

    mounted() {
        this._prevLoggedIn = this.isLoggedIn; // >>> initial setzen
        this._loginEventSent = !!this.isLoggedIn; // <<< wichtig
        this.init();
    },

    methods: {
        async getRegionPackages() {
            if (this.region === 'de') {
                return;
            }

            if (window.meta.authenticated) {
                this.$store.dispatch('abonnements/index').then(() => {
                    const abos = this.$store.getters['abonnements/get'];
                    const allRegionsAreDE = Array.isArray(abos.data) && abos.data?.every((item) => item.region === 'de');

                    if (allRegionsAreDE) {
                        this.displayWarningAboMsg = true;
                    }
                });
            }
        },

        async loadCart() {
            await this.$store.dispatch('cart/index');

            await this.$store.dispatch('cart/autoCoupon').then((response) => {
                this.autoCoupon = response;

                if (response != null) {
                    this.coupon = response;
                }
            });
        },

        goToTop() {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        },

        changeTab(tab) {
            const from = this.currentTab;

            const needsPaymentInfo = tab === 'Overview' && this.getPaymentMethods?.data.length === 0 && !this.selectedNewPaymentMethod;
            this.errorMsg = '';

            if (needsPaymentInfo) {
                this.$toast.error({
                    component: Toastification,
                    props: {
                        message: this.$t('missing_payment_information')
                    }
                });
                this.errorMsg = this.$t('missing_payment_information');

                return;
            }

            if (tab === 'Payment') {
                const { country_id, location, street, zipcode } = this.user.customer;
                const { email, last_name, first_name } = this.user;

                if ([country_id, location, street, zipcode, email, last_name, first_name].includes(null)) {
                    this.checkoutAccountDataError = true;
                    this.$toast.error({
                        component: Toastification,
                        props: {
                            message: this.$t('missing_customer_information')
                        }
                    });
                    this.errorMsg = this.$t('missing_customer_information');

                    return;
                }
            }

            this.checkoutAccountDataError = false;
            this.currentTab = tab;
            this.goToTop();

            // >>> BEGIN_CHECKOUT: Wechsel von Login -> Register
            if (from === 'CheckoutLogin' && tab === 'CheckoutRegister') {
                this.pushBeginCheckout();
            }

            // >>> REGISTER (Custom): Abschluss der Registrierung – Wechsel von Register -> UserProfile
            if (from === 'CheckoutRegister' && tab === 'UserProfile') {
                this.pushRegisterCompleted();
            }
        },

        onChangeUserState() {
            // if payment_method is passed set it
            if (this.$route.params.payment_method_id) {
                this.selectedExistingPaymentMethod = this.$route.params.payment_method_id;
            }

            this.isLoggedIn ? this.changeTab('UserProfile') : this.changeTab('CheckoutLogin');
        },

        closePopupAndApplyCoupon() {
            this.$emit('update:open', (this.showAutoCouponPopup = !this.showAutoCouponPopup));
            this.applyCoupon();
        },

        disableAutoCoupon() {
            this.$emit('update:open', (this.showAutoCouponPopup = !this.showAutoCouponPopup));
            this.autoCoupon = null;
        },

        applyCoupon() {
            api.post('/cart/coupon', {
                code: this.coupon
            })
                .then((response) => {
                    if (response.status === 204) {
                        this.$toast.error(this.$t('coupon_not_valid'));
                        this.loading = false;
                    } else {
                        this.$store.dispatch('cart/index');
                        this.$toast.success(this.$t('coupon_valid'));
                        this.loading = false;
                    }
                })
                .catch(() => {
                    this.$toast.error(this.$t('coupon_error'));
                    this.loading = false;
                });
        },

        async verifyUserData() {
            this.loading = true;
            const user = this.user;

            if (!user.meta.notifications.newsletters) {
                user.meta.notifications.newsletters = this.registerForNewsletter;
            }

            if (!user.email_confirmation) {
                user.email_confirmation = user.email;
            }

            return await this.$store
                .dispatch('user/update', user)
                .then((response) => Promise.resolve(response))
                .catch((error) => {
                    this.$toast.error({
                        component: Toastification,
                        props: {
                            message: error?.response?.data?.message
                        }
                    });

                    return Promise.reject(error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        updateEditingProfile(payload) {
            this.editingProfile = payload;

            this.checkoutAccountDataError = false;

            // Stop showing account errors hoping the user has fixed them.
            if (this.editingProfile === false) {
                this.accountErrors = false;
            }
        },

        selectExistingPaymentMethod(paymentMethod) {
            if (paymentMethod) {
                this.selectedExistingPaymentMethod = paymentMethod;
                this.selectedNewPaymentMethod = null;
                this.paymentMethodSelected = true;

                this.pushAddPaymentInfo(paymentMethod);
            } else if (!paymentMethod && !this.selectedNewPaymentMethod) {
                this.paymentMethodSelected = false;
            }
        },

        selectNewPaymentMethod(paymentMethod) {
            if (paymentMethod) {
                this.selectedNewPaymentMethod = paymentMethod;
                this.selectedExistingPaymentMethod = null;
                this.paymentMethodSelected = true;

                this.pushAddPaymentInfo(paymentMethod);
            } else if (!this.selectedExistingPaymentMethod && !paymentMethod) {
                this.paymentMethodSelected = false;
            }
        },

        async processPayment() {
            // check if terms and conditions is selected
            this.conditionsNotAcceptedError = false;
            this.termsNotAcceptedError = false;
            this.wheelTermsNotAcceptedError = false;

            if (
                this.acceptedTermsConditions === false ||
                this.acceptedConditions === false ||
                (this.region === 'de' && this.acceptedWheelConditions === false && this.wheel?.minOrderValue <= this.cartTotalGross)
            ) {
                if (this.acceptedTermsConditions === false) this.termsNotAcceptedError = true;
                if (this.acceptedConditions === false) this.conditionsNotAcceptedError = true;
                if (this.region === 'de' && this.acceptedWheelConditions === false) this.wheelTermsNotAcceptedError = true;

                this.$scrollToId('#terms');
                return;
            }

            await this.verifyUserData();

            // Checking if there is an auto coupon and is not added to cart
            const activeCoupon = this.$store.getters['cart/getCouponCode'];
            if (this.autoCoupon && activeCoupon == null) {
                this.showAutoCouponPopup = true;
                this.coupon = this.autoCoupon;
                return;
            }

            // **NEU: Vor Redirect/Submit sichern**
            this.savePurchasePayloadForSuccess();

            // optional: End-Checkout Event
            this.pushEndCheckout();

            // jetzt erst Zahlung abschicken / Redirect zu Saferpay
            this.selectedExistingPaymentMethod ? this.$refs.existingPaymentMethodForm.submit() : this.$refs.newPaymentMethodForm.submit();
        },

        ...mapActions('cart', ['index']),
        async init() {
            this.onChangeUserState();
            await this.loadCart();
            await this.getRegionPackages();

            // <<< view_cart
            if (this.getItems?.length) {
                this.pushViewCart(this.mapTabToListName(this.currentTab));
            }
        },

        // ---- GA4 HELPERS ----
        buildGaItemFromCartItem(cartItem, index = 0) {
            const p = cartItem?.product;
            const pv = cartItem?.productVariant;

            // Variante/Perioden-Titel
            const pavPeriod = pv?.productAttributeValues?.data?.find((av) => av?.meta?.type === 'period');
            const variantTitle = (pavPeriod?.title?.de ?? pavPeriod?.title ?? 'Variante').toString();

            return {
                // item_id = Variant-ID (dein Wunsch)
                item_id: pv?.id,
                item_name: p?.name?.de ?? p?.name ?? 'Produkt',
                item_category: p?.productCategories?.data?.[0]?.name?.de ?? p?.productCategories?.data?.[0]?.name ?? 'Kategorie',
                item_variant: variantTitle,
                price: Number(pv?.total_end_price_gross ?? pv?.base_price_gross ?? 0),
                currency: (this.$getCurrencyCode && this.$getCurrencyCode()) || 'EUR',
                quantity: cartItem?.quantity ?? 1,
                index
            };
        },

        getCartEcommercePayload() {
            const items = (this.getItems || []).map((ci, i) => this.buildGaItemFromCartItem(ci, i)).filter(Boolean);
            const currency = items[0]?.currency || 'EUR';
            const value = items.reduce((s, it) => s + Number(it.price) * Number(it.quantity || 1), 0);

            return { currency, value, items };
        },

        userStatusSuffix() {
            return this.isLoggedIn ? ' — Logged In' : ' — Guest';
        },

        mapTabToListName(tab) {
            const base = (() => {
                switch (tab) {
                    case 'CheckoutLogin':
                        return 'Checkout – Login';
                    case 'CheckoutRegister':
                        return 'Checkout – Register';
                    case 'UserProfile':
                        return 'Checkout – Customer Data';
                    case 'Payment':
                        return 'Checkout – Payment';
                    case 'Overview':
                        return 'Checkout – Overview';
                    default:
                        return 'Checkout';
                }
            })();
            return base + this.userStatusSuffix();
        },

        pushViewCart(listName = 'Checkout') {
            const ec = this.getCartEcommercePayload();
            if (!ec.items?.length) return;

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ ecommerce: null });
            window.dataLayer.push({
                event: 'view_cart',
                ecommerce: {
                    item_list_name: listName,
                    ...ec
                }
            });
        },

        pushLoginStatus() {
            const ec = this.getCartEcommercePayload();

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ ecommerce: null }); // sauber resetten
            window.dataLayer.push({
                event: 'login',
                method: 'email', // optional
                user_status: 'logged_in', // optional (eigene CD)
                ecommerce: {
                    item_list_name: this.mapTabToListName(this.currentTab),
                    ...ec
                }
            });
        },

        pushBeginCheckout() {
            const ec = this.getCartEcommercePayload();
            if (!ec.items?.length) return;

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ ecommerce: null });
            window.dataLayer.push({
                event: 'begin_checkout',
                ecommerce: {
                    item_list_name: 'Checkout – Register',
                    ...ec
                }
            });
        },

        pushRegisterCompleted() {
            const ec = this.getCartEcommercePayload();
            if (!ec.items?.length) return;

            const userType = this.$store.getters['user/isBusinessUser']() ? 'business' : 'private';

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ ecommerce: null });
            window.dataLayer.push({
                // Custom Event (nicht GA4-Standard)
                event: 'register',
                ecommerce: {
                    item_list_name: 'Checkout – Register',
                    ...ec
                },
                user_data: {
                    customer_type: userType
                }
            });
        },

        normalizePaymentType(pm) {
            // 1) Falls direkt ein Label/ID übergeben wird
            const asString = (v) => (v == null ? '' : String(v)).toLowerCase().trim();

            // Mapping exakt deiner UI-Texte -> GA4 payment_type
            const labelMap = new Map([
                ['kreditkarte', 'card'],
                ['paypal', 'paypal'],
                ['google pay', 'google_pay'],
                ['apple pay', 'apple_pay'],
                ['apple pay (nur in safari nutzbar)', 'apple_pay']
            ]);

            // a) Nur String/ID?
            if (typeof pm === 'string' || typeof pm === 'number') {
                const raw = asString(pm);
                // exakte Treffer
                if (labelMap.has(raw)) return labelMap.get(raw);
                // Fallback: heuristik
                if (raw.includes('paypal')) return 'paypal';
                if (raw.includes('google')) return 'google_pay';
                if (raw.includes('apple')) return 'apple_pay';
                if (raw.includes('kredit') || raw.includes('karte') || raw.includes('card') || raw.includes('visa') || raw.includes('master')) return 'card';
                return 'other';
            }

            // b) Objekt: relevante Felder einsammeln
            const parts = [];
            if (pm && typeof pm === 'object') {
                ['label', 'name', 'title', 'type', 'code', 'provider', 'gateway', 'brand', 'scheme', 'id'].forEach((k) => {
                    if (pm[k]) parts.push(asString(pm[k]));
                });
                if (pm.meta && typeof pm.meta === 'object') {
                    Object.values(pm.meta).forEach((v) => v && parts.push(asString(v)));
                }
            }
            const raw = parts.join(' ').trim();

            // Exakte Label-Treffer zuerst
            for (const [lbl, mapped] of labelMap.entries()) {
                if (raw === lbl || raw.includes(lbl)) return mapped;
            }

            // Heuristik/Fallbacks
            if (raw.includes('paypal')) return 'paypal';
            if (raw.includes('google')) return 'google_pay';
            if (raw.includes('apple')) return 'apple_pay';
            if (raw.includes('kredit') || raw.includes('karte') || raw.includes('card') || raw.includes('visa') || raw.includes('master')) return 'card';

            return 'other';
        },

        pushAddPaymentInfo(paymentMethod) {
            const ec = this.getCartEcommercePayload();
            if (!ec.items?.length) return;

            const payment_type = this.normalizePaymentType(paymentMethod);

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ ecommerce: null });
            window.dataLayer.push({
                event: 'add_payment_info',
                ecommerce: {
                    item_list_name: 'Checkout – Payment',
                    payment_type,
                    ...ec
                }
            });
        },

        getCustomerCountryCode() {
            // Versucht mehrere Felder; fallback auf Region
            const u = this.user || {};
            const c = u.customer || {};
            const fromCustomer =
                c.country_code ||
                c.country ||
                (c.country_obj && (c.country_obj.iso_code || c.country_obj.code)) ||
                (typeof c.country_id === 'string' && c.country_id.length === 2 ? c.country_id : null);

            const guessFromRegion = (this.region || 'de').toUpperCase();
            const raw = (fromCustomer || '').toString().trim().toUpperCase();

            // normalize: DEU -> DE, Deutschland -> DE etc. (soft)
            if (raw.length === 2) return raw;
            if (/^DEU|DEUTSCH/i.test(raw)) return 'DE';
            if (/^AUT|ÖSTER/i.test(raw)) return 'AT';
            if (/^CHE|SCHWEIZ|SUISSE/i.test(raw)) return 'CH';

            return guessFromRegion;
        },

        getActivePaymentMethod() {
            return this.selectedExistingPaymentMethod || this.selectedNewPaymentMethod || null;
        },

        pushEndCheckout() {
            const ec = this.getCartEcommercePayload();
            if (!ec.items?.length) return;

            const coupon = this.$store.getters['cart/getCouponCode'] || null;

            const customer_type = this.$store.getters['user/isBusinessUser']() ? 'business' : 'private';
            const customer_country = this.getCustomerCountryCode();

            const paymentMethod = this.getActivePaymentMethod();
            const payment_type = this.normalizePaymentType(paymentMethod);

            const terms_accepted = !!this.acceptedTermsConditions; // AGB (Widerrufsinfo vor Ausführung)
            const widerruf_accepted = !!this.acceptedConditions; // Widerrufsrecht gelesen/akzeptiert
            const wheel_accepted = this.region === 'de' && this.wheel?.minOrderValue <= this.cartTotalGross ? !!this.acceptedWheelConditions : null;

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ ecommerce: null }); // sauber resetten

            window.dataLayer.push({
                event: 'end_checkout',
                ecommerce: {
                    currency: ec.currency,
                    value: ec.value,
                    ...(coupon ? { coupon } : {}),
                    items: ec.items
                },
                customer_data: {
                    payment_type,
                    customer_type,
                    customer_country,
                    terms_accepted,
                    widerruf_accepted,
                    wheel_accepted
                }
            });
        },

        savePurchasePayloadForSuccess() {
            const ec = this.getCartEcommercePayload();
            const coupon = this.$store.getters['cart/getCouponCode'] || null;

            const customer_type = this.$store.getters['user/isBusinessUser']() ? 'business' : 'private';
            const customer_country = this.getCustomerCountryCode();

            const paymentMethod = this.selectedExistingPaymentMethod || this.selectedNewPaymentMethod || null;
            const payment_type = this.normalizePaymentType(paymentMethod);

            const payload = {
                transaction_id: null, // falls du jetzt noch keine Order-ID hast
                currency: ec.currency || 'EUR',
                value: ec.value || 0,
                coupon,
                items: ec.items || [],
                customer_data: { payment_type, customer_type, customer_country },
                _savedAt: Date.now() // TTL/Debug
            };

            try {
                const json = JSON.stringify(payload);
                // -> **localStorage zuerst**, session als Fallback
                localStorage.setItem('ga_purchase', json);
                sessionStorage.setItem('ga_purchase', json);
            } catch (e) {
                console.warn('[GA] savePurchasePayloadForSuccess failed:', e);
            }
        }
    }
};
</script>
